import { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, List, ListItemButton, Stack, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { EYEBROW_SX, codeSx } from '../../../generic/qr/qrReadingStyles'
import JobStatusChip from '../FabricationOrderJobReference/PlannedJobs/components/JobStatusChip/JobStatusChip'
import MosSmartDialog from '../common/MosSmartDialog'
import { JOB_ORDER_CRITERIA } from './qrLookupConfig'
import { jobName } from './qrJobText'

/**
 * The jobs a read could open, when the station asked to choose (askOperatorOnMultipleJobs, RN13, RN30).
 *
 * In the order Sun sent them — that order is the station's job order criterion, so re-sorting here
 * would quietly contradict it. The first one is the job the read would have opened without asking,
 * so it is highlighted and says why it comes first. Each row is the button: one tap opens that job.
 *
 * A long list gets a filter by code or name, over what already arrived: Sun sends every candidate, so
 * filtering here neither saves a request nor hides a job Sun did not send (R24). It never re-sorts,
 * and hiding the first row marks no other one in its place — the mark is about the order, not about
 * what happens to be on top.
 */

// Up to this many rows the whole list fits at a glance; past it the filter shows up.
const FILTER_THRESHOLD = 8

// Trimmed, without case and without accents: "Acábado" is found typing "acab".
const searchable = (text = '') => text.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

const matchesFilter = (job, filter) => {
  const wanted = searchable(filter)
  return wanted === '' || searchable(job.code).includes(wanted) || searchable(job.name).includes(wanted)
}

// "Planned at <machine> · step 2 of 4", or whichever half Sun sent; '' with neither.
const metaLine = (job, t_) => {
  const plannedAt = job.plannedDevice?.id ? job.plannedDevice.name : ''
  const hasStep = job.step > 0
  if (plannedAt !== '' && hasStep) {
    return t_('Planned at {{machine}} · step {{step}} of {{steps}}', { machine: plannedAt, step: job.step, steps: job.steps })
  }
  if (plannedAt !== '') return t_('Planned at {{machine}}', { machine: plannedAt })
  if (hasStep) return t_('Step {{step}} of {{steps}}', { step: job.step, steps: job.steps })
  return ''
}

const QrDisambiguationDialog = ({ open, orderCode = '', jobs = [], jobOrderCriterion = JOB_ORDER_CRITERIA.PLAN, onSelect, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [filter, setFilter] = useState('')
  const filterable = jobs.length > FILTER_THRESHOLD
  const firstJobId = jobs.length > 0 ? jobs[0].jobReferenceId : 0
  const visibleJobs = filterable ? jobs.filter((job) => matchesFilter(job, filter)) : jobs
  const nextMark = jobOrderCriterion === JOB_ORDER_CRITERIA.PRIORITY ? t_('Next by priority') : t_('Next by plan')

  return (
    <MosSmartDialog
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      paperWidth='min(34rem, 96vw)'
      title={{
        visible: true,
        icon: null,
        render: (
          <Box data-testid='list-header' sx={{ textAlign: 'left' }}>
            {orderCode !== '' && <Typography sx={EYEBROW_SX}>{orderCode}</Typography>}
            <Typography variant='h6' color='primary.main'>
              {t_('{{count}} jobs this machine can do', { count: jobs.length })}
            </Typography>
          </Box>
        )
      }}
    >
      <Stack gap={1.5} sx={{ width: '100%', textAlign: 'left' }}>
        {filterable && (
          <TextField
            type='search'
            size='small'
            fullWidth
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t_('Filter by code or name')}
            inputProps={{ 'aria-label': t_('Filter by code or name') }}
          />
        )}

        <List disablePadding>
          {visibleJobs.map((job) => {
            const isNext = job.jobReferenceId === firstJobId
            const name = jobName(job)
            const meta = metaLine(job, t_)
            return (
              <ListItemButton
                key={job.jobReferenceId}
                aria-label={t_('Open job detail') + ' ' + job.code}
                data-next={isNext ? 'true' : 'false'}
                onClick={() => onSelect(job)}
                sx={(theme) => ({
                  gap: 1.5,
                  minHeight: '3.75rem',
                  borderRadius: '0.75rem',
                  mb: 0.75,
                  border: '1px solid',
                  borderColor: isNext ? 'primary.main' : 'divider',
                  backgroundColor: isNext ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                })}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box component='span' sx={codeSx('1rem')}>{job.code}</Box>
                  {name !== '' && <Typography noWrap>{name}</Typography>}
                  {isNext && (
                    <Typography variant='caption' color='primary.main' sx={{ display: 'block', fontWeight: 700 }}>{nextMark}</Typography>
                  )}
                  {meta !== '' && (
                    <Typography variant='body2' color='text.secondary' noWrap>{meta}</Typography>
                  )}
                </Box>
                <JobStatusChip status={job.status} statusType={job.status} />
                <ChevronRightRoundedIcon color='primary' />
              </ListItemButton>
            )
          })}
        </List>

        {visibleJobs.length === 0 && filter !== '' && (
          <Typography color='text.secondary'>{t_('No jobs match the filter')}</Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='outlined' color='inherit' onClick={onClose}>{t_('Cancel')}</Button>
        </Box>
      </Stack>
    </MosSmartDialog>
  )
}

QrDisambiguationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The order whose jobs these are, shown above the title. */
  orderCode: PropTypes.string,
  /** matchingJobs as Sun ordered them. */
  jobs: PropTypes.arrayOf(PropTypes.shape({
    jobReferenceId: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    plannedDevice: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    step: PropTypes.number,
    steps: PropTypes.number
  })),
  /** The station's job order: it says why the first row comes first. */
  jobOrderCriterion: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default QrDisambiguationDialog
